// Games Expansion V1: authored content only. No backend calls, no
// user-specific data. Every game reads from this file and nothing else.
//
// Content architecture: plain data (arrays/objects), not JSX, so the
// scoring/interaction logic in each game component stays generic and this
// file can grow (more quiz questions, more scenes, more word lists)
// without touching component code.

// ---------------------------------------------------------------------------
// Game catalogue (drives the Games hub cards)
// ---------------------------------------------------------------------------
export const GAMES_CATALOGUE = [
  {
    id: "spot-the-issue",
    path: "/academy/games/spot-the-issue",
    title: "Spot the Issue",
    tagline: "Tap the problems before you'd ever drive off",
    description: "Two short scenes: a pickup inspection and a before/after comparison. Find what a careful renter would catch.",
  },
  {
    id: "quick-quiz",
    path: "/academy/games/quick-quiz",
    title: "Quick Quiz",
    tagline: "8 practical questions, no login needed",
    description: "Short, factual questions about confirmations, pickup/return checks, and spotting a risky listing.",
  },
  {
    id: "word-finder",
    path: "/academy/games/word-finder",
    title: "Word Finder",
    tagline: "Find the rental vocabulary in the grid",
    description: "A simple word search using the terms you'll actually see on a ConfirmPH booking.",
  },
];

// ---------------------------------------------------------------------------
// Quick Quiz: fully client-side, stateless. Every field (including the
// correct answer) ships to the browser; unlike Academy's scored safety-
// rating quizzes, there is no answer key worth protecting here, so there is
// no reason to pay for a server round-trip per question. See the Games
// Expansion V1 audit for the full reasoning.
// ---------------------------------------------------------------------------
export const QUIZ_QUESTIONS = [
  {
    id: "q1",
    question: "What should you verify before pickup?",
    options: [
      "Nothing: the business handles everything",
      "The vehicle's condition, fuel level, and odometer reading match what was agreed",
      "Only the price",
      "The weather forecast",
    ],
    correctIndex: 1,
    explanation: "Checking condition, fuel, and odometer at pickup gives you a clear starting point, so any question later is easy to resolve.",
  },
  {
    id: "q2",
    question: "What does “Confirmed” actually mean on ConfirmPH?",
    options: [
      "The business has taken payment in full",
      "Both sides have a written, agreed record of the booking details",
      "The vehicle is guaranteed available forever",
      "ConfirmPH insures the rental",
    ],
    correctIndex: 1,
    explanation: "ConfirmPH confirms both sides agree on the same written details: it does not process payments or guarantee outcomes.",
  },
  {
    id: "q3",
    question: "Why should damage be recorded at pickup?",
    options: [
      "It's not necessary if you trust the business",
      "So there's a clear reference point to compare against at return",
      "Only businesses need to do this",
      "To increase the price",
    ],
    correctIndex: 1,
    explanation: "A pickup record protects both sides: it's the baseline everyone can point back to.",
  },
  {
    id: "q4",
    question: "What should you check before returning a vehicle?",
    options: [
      "Nothing: just hand over the keys",
      "Fuel level, cleanliness, and that nothing new is damaged",
      "Only the mileage",
      "That the business is open",
    ],
    correctIndex: 1,
    explanation: "Checking these before return helps avoid a surprise charge or dispute after you've already left.",
  },
  {
    id: "q5",
    question: "When should you request a booking change?",
    options: [
      "Never: bookings can't change",
      "As soon as you know your dates, vehicle, or details need to be different, before pickup if possible",
      "Only after the rental is complete",
      "Only through social media",
    ],
    correctIndex: 1,
    explanation: "Requesting a change early gives the business time to accommodate it and keeps your written record accurate.",
  },
  {
    id: "q6",
    question: "What should you do if vehicle details don't match what was agreed?",
    options: [
      "Say nothing and hope it's fine",
      "Raise it with the business right away and get it noted before you proceed",
      "Post about it online immediately",
      "Assume it's your fault",
    ],
    correctIndex: 1,
    explanation: "Flagging a mismatch immediately, before you drive off: is the clearest way to protect yourself.",
  },
  {
    id: "q7",
    question: "Why keep a written booking record instead of relying on a verbal agreement?",
    options: [
      "Written records are required by law",
      "Verbal agreements are easy to misremember or dispute later: a written record is harder to argue with",
      "It looks more professional",
      "There's no real difference",
    ],
    correctIndex: 1,
    explanation: "Memory fades and details get fuzzy: a written record is what both sides can refer back to.",
  },
  {
    id: "q8",
    question: "What's a red flag that might indicate a risky or scam rental listing?",
    options: [
      "The business asks you to confirm details in writing",
      "Pressure to pay the full price immediately with no way to verify the vehicle or business first",
      "The business has reviews",
      "The business responds to messages",
    ],
    correctIndex: 1,
    explanation: "Legitimate businesses are generally comfortable with you verifying details before paying in full: pressure with no verification is a warning sign.",
  },
];

// ---------------------------------------------------------------------------
// Spot the Issue: authored scenes. Each hotspot is stored as x_pct/y_pct
// (percentage of the scene container, 0-100), the exact convention already
// established by VehicleDamageMap.jsx for vehicle damage markers, chosen so
// hotspots stay accurate on any screen size. `radiusPct` is the hit-test
// tolerance (percentage of the container's smaller dimension).
//
// Scenes reuse the same simple flat-outline car SVG style already used by
// CarDiagram.jsx/VehicleDamageMap.jsx (no photographs: see the Games
// Expansion V1 audit's asset recommendation) via SceneIllustration.jsx.
// ---------------------------------------------------------------------------
export const SPOT_THE_ISSUE_SCENES = [
  {
    id: "pickup-front",
    title: "Pickup inspection: front view",
    intro: "You've just arrived to pick up the car. Tap every problem a careful renter would flag before driving off.",
    view: "front",
    issues: [
      { id: "bumper", x_pct: 50, y_pct: 84, radiusPct: 10, mark: "scratch", label: "Cracked/scuffed bumper", explanation: "Existing bumper damage should be noted and photographed before you take the vehicle: otherwise it can look like something you caused." },
      { id: "headlight", x_pct: 73, y_pct: 68, radiusPct: 9, mark: "crack", label: "Damaged headlight", explanation: "A cracked light housing is easy to miss in a quick glance: worth a close look at both headlights." },
      { id: "tire", x_pct: 26, y_pct: 90, radiusPct: 10, mark: "flat", label: "Low or damaged tire", explanation: "A visibly low tire is a safety issue, not just a cosmetic one: raise it before you leave the lot." },
      { id: "plate", x_pct: 50, y_pct: 74, radiusPct: 9, mark: "mismatch", label: "Plate doesn't match the booking details", explanation: "If the plate on the car doesn't match what's on your confirmed booking, ask before you proceed: it may be the wrong vehicle." },
      { id: "warning-light", x_pct: 50, y_pct: 44, radiusPct: 9, mark: "warning", label: "Dashboard warning light visible", explanation: "A warning light already on before you've even started the engine is worth asking about." },
    ],
  },
  {
    id: "return-interior",
    title: "Return inspection: interior view",
    intro: "You're about to hand the car back. Spot what should be checked and recorded before you leave.",
    view: "interior",
    issues: [
      { id: "fuel", x_pct: 53, y_pct: 42, radiusPct: 9, mark: "fuel", label: "Fuel gauge reads near-empty", explanation: "If the agreement said return with a full tank, an empty gauge here matters: check it together with staff." },
      { id: "odometer", x_pct: 30, y_pct: 40, radiusPct: 9, mark: "mismatch", label: "Odometer reading looks off", explanation: "Compare the return odometer reading to what was recorded at pickup: a big unexplained jump is worth a question." },
      { id: "seat-stain", x_pct: 28, y_pct: 64, radiusPct: 10, mark: "dirt", label: "New stain on the driver's seat", explanation: "New interior stains or dirt should be pointed out at return, not discovered later by the business." },
      { id: "missing-doc", x_pct: 50, y_pct: 64, radiusPct: 9, mark: "missing", label: "Missing document/parking card", explanation: "If something that came with the car is missing, it's better raised in person than discovered afterward." },
      { id: "back-seat", x_pct: 50, y_pct: 86, radiusPct: 10, mark: "dirt", label: "Stain on the back seat", explanation: "Check the back seat too, not just the front: it's easy to overlook." },
    ],
  },
];

// Find the Difference: a variant of Spot the Issue: two versions of the
// same scene (pickup vs. return), differences authored explicitly rather
// than detected. Never used to assign fault: purely "what changed."
export const FIND_THE_DIFFERENCE_SCENE = {
  id: "pickup-vs-return-side",
  title: "Pickup vs. return: side view",
  intro: "Left: how the car looked at pickup. Right: how it looked at return. Find what changed: this is about noticing differences, not deciding who's at fault.",
  view: "left",
  differences: [
    { id: "door-scratch", x_pct: 45, y_pct: 63, radiusPct: 10, mark: "scratch", label: "New scratch on the door", explanation: "A mark that wasn't in the pickup record but appears at return is exactly what a condition record is for: it's there to be discussed, not assumed." },
    { id: "mirror", x_pct: 25, y_pct: 50, radiusPct: 9, mark: "missing", label: "Side mirror missing", explanation: "A missing accessory is easy to notice side-by-side but easy to miss if you're not comparing carefully." },
    { id: "rear-tire", x_pct: 76, y_pct: 88, radiusPct: 10, mark: "flat", label: "Rear tire looks low", explanation: "Comparing tire condition between pickup and return can catch something that happened during the rental." },
    { id: "rear-dent", x_pct: 87, y_pct: 68, radiusPct: 9, mark: "dent", label: "New mark near the rear bumper", explanation: "Small new marks near bumpers are common and easy to miss: worth a careful look at both ends of the car." },
  ],
};

// ---------------------------------------------------------------------------
// Word Finder: deterministic 12x12 grid, computed and verified offline
// (not generated at runtime) so layout is stable and testable. Directions
// used: right, down, diagonal-down-right, diagonal-up-right only (no
// reversed/backwards words, to keep the game approachable).
// ---------------------------------------------------------------------------
export const WORD_SEARCH_GRID = [
  ["H","B","I","N","S","P","E","C","T","I","O","N"],
  ["T","C","O","W","X","Y","Z","A","D","C","D","E"],
  ["F","R","O","O","J","K","L","M","E","O","O","Q"],
  ["R","E","T","N","K","W","X","Y","P","A","M","C"],
  ["D","V","F","G","F","I","J","K","O","M","E","P"],
  ["P","I","R","S","R","I","N","W","S","Y","T","I"],
  ["B","E","D","E","F","E","R","G","I","K","E","C"],
  ["N","W","P","Q","R","S","C","M","T","W","R","K"],
  ["Z","V","E","H","I","C","L","E","E","I","J","U"],
  ["L","M","R","E","T","U","R","N","I","D","V","P"],
  ["X","Y","Z","A","B","C","D","E","F","P","H","I"],
  ["J","K","L","D","A","M","A","G","E","S","T","U"],
];

export const WORD_SEARCH_WORDS = [
  { word: "CONFIRMED", path: [[1,1],[2,2],[3,3],[4,4],[5,5],[6,6],[7,7],[8,8],[9,9]] },
  { word: "DEPOSIT", path: [[1,8],[2,8],[3,8],[4,8],[5,8],[6,8],[7,8]] },
  { word: "PICKUP", path: [[4,11],[5,11],[6,11],[7,11],[8,11],[9,11]] },
  { word: "RETURN", path: [[9,2],[9,3],[9,4],[9,5],[9,6],[9,7]] },
  { word: "DAMAGE", path: [[11,3],[11,4],[11,5],[11,6],[11,7],[11,8]] },
  { word: "ODOMETER", path: [[0,10],[1,10],[2,10],[3,10],[4,10],[5,10],[6,10],[7,10]] },
  { word: "VEHICLE", path: [[8,1],[8,2],[8,3],[8,4],[8,5],[8,6],[8,7]] },
  { word: "BOOKING", path: [[0,1],[1,2],[2,3],[3,4],[4,5],[5,6],[6,7]] },
  { word: "INSPECTION", path: [[0,2],[0,3],[0,4],[0,5],[0,6],[0,7],[0,8],[0,9],[0,10],[0,11]] },
  { word: "RECEIPT", path: [[5,4],[6,5],[7,6],[8,7],[9,8],[10,9],[11,10]] },
  { word: "REVIEW", path: [[2,1],[3,1],[4,1],[5,1],[6,1],[7,1]] },
];
